/**
 * Helpers for working with satellite product cloud cover values.
 *
 * Backend returns cloud cover either as a fraction in the [0, 1] range or
 * directly as a percentage. These helpers normalize the raw payload into a
 * rounded percentage and provide a demo fallback for products that do not
 * report cloud cover.
 */

/**
 * The `maxCloudCover` filter slider is stepped by 5 (0..100). Stepping keeps
 * the request value aligned with the UI control.
 */
const MAX_CLOUD_COVER_STEP = 5;
const MAX_CLOUD_COVER_LIMIT = 100;

/**
 * When the backend does not report cloud cover we synthesize a demo value so
 * the UI does not show "No data" for every such product. 26% is an arbitrary
 * upper bound chosen to keep the placeholder looking plausible.
 */
const RANDOM_CLOUD_COVER_UPPER_BOUND = 26;

/**
 * Normalizes a raw cloud cover value into a percentage rounded to 2 decimals,
 * or `null` when the value is absent or cannot be interpreted as a number.
 *
 * Values in the [0, 1] range are treated as a fraction and converted to a
 * percentage; any other finite number is assumed to already be a percentage.
 */
export function normalizeCloudCover(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  const percentValue = numericValue >= 0 && numericValue <= 1 ? numericValue * 100 : numericValue;
  return Math.round(percentValue * 100) / 100;
}

/**
 * Returns a random demo cloud cover percentage (rounded to 2 decimals) used as
 * a placeholder when a product does not report cloud cover. The result never
 * exceeds `maxCloudCover` (the active filter) nor the demo upper bound.
 */
export function getRandomCloudCover(maxCloudCover: number = MAX_CLOUD_COVER_LIMIT): number {
  const upperBound = Math.max(0, Math.min(RANDOM_CLOUD_COVER_UPPER_BOUND, maxCloudCover));
  return Math.round(Math.random() * upperBound * 100) / 100;
}

/**
 * Clamps and steps a raw slider/input value to a valid maxCloudCover percentage
 * in the [0, 100] range, snapped to the nearest multiple of 5.
 */
export function normalizeMaxCloudCoverInput(value: unknown): number {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return MAX_CLOUD_COVER_LIMIT;
  }

  const steppedValue = Math.round(numericValue / MAX_CLOUD_COVER_STEP) * MAX_CLOUD_COVER_STEP;
  return Math.min(MAX_CLOUD_COVER_LIMIT, Math.max(0, steppedValue));
}

/**
 * Converts the active maxCloudCover filter into the value sent to the backend.
 * A filter of 100% means "no restriction", so it is omitted (`undefined`) from
 * the request payload.
 */
export function getMaxCloudCoverPayloadValue(value: unknown): number | undefined {
  const maxCloudCover = normalizeMaxCloudCoverInput(value);
  return maxCloudCover < MAX_CLOUD_COVER_LIMIT ? maxCloudCover : undefined;
}
